import React from "react";
import { Unplug, CircleX, HelpCircle, FileWarning, Trash2 } from "lucide-react";

import { Gravity, MatterBody } from "../ui/gravity";

const CARDS = [
  {
    icon: Unplug,
    title: "Disconnected tools",
    body: "Erasure, assessment, reimaging, grading, inventory, and resale often happen in separate systems that don't talk to each other.",
  },
  {
    icon: CircleX,
    title: "Failed processing attempts",
    body: "Devices fail mid-workflow because of MDM locks, boot issues, missing drivers, storage problems, or setup errors — after time has already been spent.",
  },
  {
    icon: HelpCircle,
    title: "Unclear device status",
    body: "Teams waste time deciding whether a device should be resold, redeployed, repaired, quarantined, recycled, or stripped for parts.",
  },
  {
    icon: FileWarning,
    title: "Fragmented reporting",
    body: "Customers and buyers want proof, but device evidence is scattered across certificates, spreadsheets, photos, and manual notes.",
  },
];

// chips drop into the mouth of the bin, so they start clustered over its centre
const TOOL_CHIPS = [
  { label: "Blancco", cls: "bg-white text-brand-plum border border-line", x: "42%", y: "12%", angle: -8 },
  { label: "Phonecheck", cls: "bg-brand-plum text-white", x: "56%", y: "12%", angle: 6 },
  { label: "Spreadsheets", cls: "bg-white text-brand-plum border border-line", x: "44%", y: "24%", angle: -4 },
  { label: "NSYS", cls: "bg-brand-magenta text-white", x: "58%", y: "22%", angle: 9 },
  { label: "Reusely", cls: "bg-brand-wash text-brand-plum", x: "42%", y: "34%", angle: 5 },
  { label: "Certificates", cls: "bg-white text-brand-plum border border-line", x: "56%", y: "32%", angle: -9 },
  { label: "Photos", cls: "bg-brand-orchid text-white", x: "48%", y: "44%", angle: 7 },
  { label: "Manual notes", cls: "bg-white text-brand-plum border border-line", x: "46%", y: "54%", angle: -6 },
  { label: "In-house scripts", cls: "bg-brand-plum text-white", x: "54%", y: "52%", angle: 4 },
];

const Problem = () => {
  return (
    <section id="problem" className="relative bg-brand-bg font-body">
      <div className="mx-auto max-w-7xl px-6 py-20 lg:py-28">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          {/* copy + cards */}
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-3.5 py-1.5 text-xs font-medium uppercase tracking-[0.16em] text-brand-plum">
              The problem
            </span>
            <h2 className="mt-5 font-display text-3xl font-semibold leading-tight tracking-tight text-brand-plum sm:text-4xl lg:text-5xl">
              IT Asset Recovery Is Still Too Manual
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-ink sm:text-lg">
              Most recovery teams still rely on disconnected tools, repeated manual checks, spreadsheets,
              technician knowledge, and fragmented reports to process used devices. That slows recovery,
              creates uncertainty, and makes it harder to turn assets into resale-ready or
              redeployment-ready inventory.
            </p>

            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {CARDS.map(({ icon: Icon, title, body }) => (
                <div
                  key={title}
                  className="rounded-2xl border border-line bg-white p-5 transition-shadow hover:shadow-lg hover:shadow-brand-plum/5"
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-brand-wash text-brand-magenta">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-3 font-display text-base font-semibold text-brand-plum">{title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted">{body}</p>
                </div>
              ))}
            </div>
          </div>

          {/* physics: today's toolchain dumped in the bin */}
          <div className="relative">
            <div className="relative h-[350px] w-full overflow-hidden rounded-3xl border border-line bg-gradient-to-br from-white to-brand-wash/50">
              <span className="absolute left-5 top-5 z-30 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.16em] text-muted">
                <Trash2 className="h-4 w-4 text-brand-magenta" />
                The toolchain today
              </span>

              {/* the bin — drawn behind the falling tools */}
              <svg
                className="pointer-events-none absolute inset-0 z-0 h-full w-full"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                aria-hidden
              >
                {/* body — bottom sits on the container's bottom line (y=100) */}
                <path d="M30,38 L34,100 Q50,102 66,100 L70,38" fill="#EAE8FF" fillOpacity="0.3" stroke="#3B2B52" strokeOpacity="0.4" strokeWidth="1.5" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                {/* open mouth */}
                <ellipse cx="50" cy="38" rx="20" ry="2.8" fill="#F5F5FF" fillOpacity="0.85" stroke="#3B2B52" strokeOpacity="0.35" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
                {/* ridges */}
                <line x1="42" y1="41" x2="44" y2="99" stroke="#B97EE9" strokeOpacity="0.4" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
                <line x1="50" y1="41" x2="50" y2="100" stroke="#B97EE9" strokeOpacity="0.4" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
                <line x1="58" y1="41" x2="56" y2="99" stroke="#B97EE9" strokeOpacity="0.4" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
              </svg>

              {/* falling tools */}
              <Gravity gravity={{ x: 0, y: 1 }} className="z-10 h-full w-full">
                {/* invisible walls that keep the pile inside the bin */}
                <MatterBody isDraggable={false} matterBodyOptions={{ isStatic: true }} x="33%" y="55%">
                  <div className="pointer-events-none h-[330px] w-[10px]" />
                </MatterBody>
                <MatterBody isDraggable={false} matterBodyOptions={{ isStatic: true }} x="67%" y="55%">
                  <div className="pointer-events-none h-[330px] w-[10px]" />
                </MatterBody>
                {TOOL_CHIPS.map((chip) => (
                  <MatterBody
                    key={chip.label}
                    matterBodyOptions={{ friction: 0.5, restitution: 0.25 }}
                    x={chip.x}
                    y={chip.y}
                    angle={chip.angle}
                  >
                    <div
                      className={`cursor-grab whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium shadow-sm ${chip.cls}`}
                    >
                      {chip.label}
                    </div>
                  </MatterBody>
                ))}
              </Gravity>

              {/* front lip — drawn over the chips so they tuck inside the mouth */}
              <svg
                className="pointer-events-none absolute inset-0 z-20 h-full w-full"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                aria-hidden
              >
                <path d="M30,38 A20,2.8 0 0 0 70,38" fill="none" stroke="#3B2B52" strokeOpacity="0.55" strokeWidth="2" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
              </svg>

              <span className="absolute bottom-4 left-5 z-30 text-[11px] text-faint">
                drag a tool to dig through the pile
              </span>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-muted">
              Disconnected, manual, and slow — today's stack of point tools belongs in the bin.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Problem;
